/**
 * The learn.4klabs.ai front door — Aurora Atlas.
 *
 * Split: the pitch on the left, the form on the right. The left column sits
 * directly on the page aurora with no glass of its own, so the wash reads as the
 * product's own light rather than as a panel; the form is the only glass on the
 * screen, which is what makes it the obvious thing to act on. The wash itself is
 * rendered by `LearnRoot subjects={LEARN_SUBJECTS}` in LearnAuthGuard, so this
 * component only owns the two columns.
 */

import { useState } from 'react';
import { readPendingSchoolCode, writePendingSchoolCode } from './pendingSchoolCode';
import { Loader2, Mail, ArrowLeft } from 'lucide-react';
import { cn } from '../../lib/utils';
import { glassHero, glassChip, inkMuted } from './learnShared';
import { LessonShowcase } from './LessonShowcase';
import {
  signInLearnerWithGoogle,
  signInLearnerWithEmail,
  registerLearnerWithEmail,
  sendLearnerPasswordReset,
  learnAuthErrorMessage,
  isLearnAuthConfigured,
} from '../../services/learnFirebase';

type Mode = 'signin' | 'signup' | 'reset';

/** The full spectrum — every learn auth surface mixes its aurora from all six. */
export const LEARN_SUBJECTS = [
  'Science',
  'Physics',
  'Chemistry',
  'Biology',
  'Mathematics',
  'Social Science',
];

/** Visible keyboard focus on every control, over glass and over the wash alike. */
const FOCUS =
  'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[3px] ' +
  'focus-visible:outline-[#5B2EE5] dark:focus-visible:outline-[#A78BFA]';

const PRIMARY = cn(
  'flex h-12 w-full items-center justify-center rounded-full bg-[#5B2EE5] text-[14.5px] font-semibold text-white',
  'shadow-[0_8px_20px_-8px_rgba(91,46,229,0.75)] transition-colors hover:bg-[#4D22D2] disabled:opacity-60',
  FOCUS,
);

/* Inputs get their own near-solid surface — bare translucency over a bright
   wash is where legibility on this direction goes to die. */
const FIELD = cn(
  'h-11 w-full rounded-xl border border-[#191831]/[0.16] bg-white/95 px-3.5 text-[14.5px] text-[#191831]',
  'outline-none transition-colors placeholder:text-[#78748F] focus:border-[#5B2EE5]',
  'dark:border-white/[0.16] dark:bg-[#0B0C15]/85 dark:text-[#EFEEF8] dark:placeholder:text-[#8B88A6]',
  FOCUS,
);

const LABEL = cn('mb-1.5 block text-[10.5px] font-bold uppercase tracking-[0.14em]', inkMuted);

const QUIET_LINK = cn(
  'rounded-md text-left transition-colors hover:text-[#5B2EE5] dark:hover:text-[#B49BFF]',
  FOCUS,
);

function GoogleGlyph() {
  return (
    <svg viewBox="0 0 24 24" className="h-[18px] w-[18px]" aria-hidden="true">
      <path fill="#4285F4" d="M23.5 12.3c0-.8-.1-1.6-.2-2.3H12v4.5h6.5a5.6 5.6 0 0 1-2.4 3.7v3h3.9c2.3-2.1 3.5-5.2 3.5-8.9z" />
      <path fill="#34A853" d="M12 24c3.2 0 5.9-1.1 7.9-2.9l-3.9-3c-1.1.7-2.5 1.2-4 1.2-3.1 0-5.7-2.1-6.6-4.9H1.4v3.1A12 12 0 0 0 12 24z" />
      <path fill="#FBBC05" d="M5.4 14.4a7.2 7.2 0 0 1 0-4.6V6.7H1.4a12 12 0 0 0 0 10.8l4-3.1z" />
      <path fill="#EA4335" d="M12 4.8c1.8 0 3.3.6 4.6 1.8l3.4-3.4A12 12 0 0 0 1.4 6.7l4 3.1C6.3 6.9 8.9 4.8 12 4.8z" />
    </svg>
  );
}

const TITLE: Record<Mode, string> = {
  signin: 'Sign in',
  signup: 'Create your account',
  reset: 'Reset your password',
};

const SUB: Record<Mode, string> = {
  signin: 'Interactive video for CBSE Classes 6–12. Sign in to keep your progress.',
  signup: 'Interactive video for CBSE Classes 6–12. Your lessons, checks, and where you left off stay with your account.',
  reset: 'Enter your email and we will send you a link to set a new password.',
};

export function LearnSignIn({ onSignedIn }: { onSignedIn?: () => void }) {
  const [mode, setMode] = useState<Mode>('signin');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  // Held in storage as it is typed: it is redeemed after sign-in, which for
  // Google happens in a popup and for email may finish in another tab.
  const [schoolCode, setSchoolCode] = useState(readPendingSchoolCode);
  const [busy, setBusy] = useState<'google' | 'email' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  if (!isLearnAuthConfigured) {
    return (
      <Shell title="Sign in" sub="Sign-in is not configured for this environment.">
        <p className={cn('mt-6 text-[14.5px] leading-relaxed', inkMuted)}>
          Ask your teacher for a class link, or try again from the school portal.
        </p>
      </Shell>
    );
  }

  const google = async () => {
    setBusy('google');
    setError(null);
    try {
      await signInLearnerWithGoogle();
      onSignedIn?.();
    } catch (e) {
      setError(learnAuthErrorMessage(e));
    } finally {
      setBusy(null);
    }
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy('email');
    setError(null);
    setNotice(null);
    try {
      if (mode === 'reset') {
        await sendLearnerPasswordReset(email);
        setNotice('Check your inbox for a reset link.');
      } else if (mode === 'signup') {
        await registerLearnerWithEmail(email, password, name);
        onSignedIn?.();
      } else {
        await signInLearnerWithEmail(email, password);
        onSignedIn?.();
      }
    } catch (err) {
      setError(learnAuthErrorMessage(err));
    } finally {
      setBusy(null);
    }
  };

  return (
    <Shell title={TITLE[mode]} sub={SUB[mode]}>
      {mode !== 'reset' && (
        <div className="mt-6">
          <label className={LABEL} htmlFor="learn-signin-school-code">
            School code <span className="font-medium normal-case tracking-normal">(if your school gave you one)</span>
          </label>
          <input
            id="learn-signin-school-code"
            className={cn(FIELD, 'uppercase tracking-[0.08em] placeholder:normal-case placeholder:tracking-normal')}
            placeholder="From your teacher"
            value={schoolCode}
            onChange={(e) => {
              setSchoolCode(e.target.value);
              writePendingSchoolCode(e.target.value);
            }}
            autoCapitalize="characters"
            autoComplete="off"
            spellCheck={false}
            maxLength={32}
          />
        </div>
      )}

      <button
        onClick={google}
        disabled={busy !== null}
        className={cn(
          'mt-5 flex h-12 w-full items-center justify-center gap-3 rounded-[14px] bg-white/95 text-[14.5px] font-semibold text-[#191831]',
          'shadow-[inset_0_0_0_1px_rgba(25,24,49,0.14),0_3px_10px_-6px_rgba(25,24,49,0.5)]',
          'transition-colors hover:bg-white disabled:opacity-60',
          'dark:bg-white/[0.10] dark:text-[#EFEEF8] dark:shadow-[inset_0_0_0_1px_rgba(255,255,255,0.18)] dark:hover:bg-white/[0.16]',
          FOCUS,
        )}
      >
        {busy === 'google' ? <Loader2 className="h-4 w-4 animate-spin" /> : <GoogleGlyph />}
        Continue with Google
      </button>

      <div className={cn('my-5 flex items-center gap-3 text-[11px] font-semibold uppercase tracking-[0.16em]', inkMuted)}>
        <span className="h-px flex-1 bg-[#191831]/[0.14] dark:bg-white/[0.14]" />
        or
        <span className="h-px flex-1 bg-[#191831]/[0.14] dark:bg-white/[0.14]" />
      </div>

      <form onSubmit={submit} className="flex flex-col gap-3">
        {mode === 'signup' && (
          <div>
            <label className={LABEL} htmlFor="learn-name">Your name</label>
            <input
              id="learn-name"
              className={FIELD}
              placeholder="Aarav Sharma"
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoComplete="name"
            />
          </div>
        )}
        <div>
          <label className={LABEL} htmlFor="learn-email">School email</label>
          <input
            id="learn-email"
            className={FIELD}
            type="email"
            required
            placeholder="aarav@school.edu.in"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="email"
          />
        </div>
        {mode !== 'reset' && (
          <div>
            <label className={LABEL} htmlFor="learn-password">Password</label>
            <input
              id="learn-password"
              className={FIELD}
              type="password"
              required
              minLength={6}
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete={mode === 'signup' ? 'new-password' : 'current-password'}
            />
          </div>
        )}

        {error && (
          <p role="alert" className="text-sm font-medium text-[#A32B16] dark:text-[#FF9C87]">
            {error}
          </p>
        )}
        {notice && (
          <p className="flex items-center gap-2 text-sm font-medium text-[#0E6B5F] dark:text-[#6FD6C4]">
            <Mail className="h-4 w-4 shrink-0" /> {notice}
          </p>
        )}

        <button type="submit" disabled={busy !== null} className={cn(PRIMARY, 'mt-2')}>
          {busy === 'email' ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : mode === 'signup' ? (
            'Create account'
          ) : mode === 'reset' ? (
            'Send reset link'
          ) : (
            'Sign in'
          )}
        </button>
      </form>

      <div className={cn('mt-5 flex flex-col items-start gap-2 text-[13.5px]', inkMuted)}>
        {mode === 'signin' && (
          <>
            <button className={QUIET_LINK} onClick={() => { setMode('signup'); setError(null); }}>
              New here? <span className="font-semibold">Create an account</span>
            </button>
            <button className={QUIET_LINK} onClick={() => { setMode('reset'); setError(null); }}>
              Forgot your password?
            </button>
          </>
        )}
        {mode !== 'signin' && (
          <button
            className={cn(QUIET_LINK, 'flex items-center gap-1.5')}
            onClick={() => { setMode('signin'); setError(null); setNotice(null); }}
          >
            <ArrowLeft className="h-3.5 w-3.5" /> Back to sign in
          </button>
        )}
      </div>
    </Shell>
  );
}

/** The card, the legend, and nothing else — the aurora belongs to LearnRoot. */
function Shell({ title, sub, children }: { title: string; sub: string; children: React.ReactNode }) {
  return (
    <div className="learn-shell mx-auto grid min-h-[calc(100vh-80px)] w-full items-center gap-10 px-5 py-10 lg:grid-cols-[1fr_460px] lg:gap-16 lg:py-14 2xl:grid-cols-[1fr_520px]">
      {/* The pitch — bare on the wash, no glass, so the light is the backdrop. */}
      <div className="hidden min-w-0 lg:block">
        <p className={cn('text-[11px] font-bold uppercase tracking-[0.2em]', inkMuted)}>4K Labs Learn</p>
        <h1 className="mt-3 max-w-[13ch] font-serif text-[54px] leading-[1.16] tracking-[-0.025em]">
          Lessons you <em className="italic text-[#5B2EE5] dark:text-[#B49BFF]">do</em>, not watch.
        </h1>
        <p className={cn('mt-5 max-w-[46ch] text-[16px] leading-[1.6]', inkMuted)}>
          Interactive video for CBSE Classes 6–12. Pause, answer, experiment — and
          watch the lesson react to what you did.
        </p>
        <ul className="mt-7 flex flex-wrap gap-2">
          {['Classes 6–12', 'Built for CBSE', 'Progress saved'].map((t) => (
            <li key={t} className={cn(glassChip, 'px-3.5 py-2 text-[12.5px] font-semibold', inkMuted)}>
              {t}
            </li>
          ))}
        </ul>

        {/* Bleeds off the LEFT edge only — a library continuing past the screen.
            A full-bleed w-screen here would blow the grid out and push the form
            off the page, which is exactly what it did the first time. */}
        <LessonShowcase
          className="mt-9"
          style={{
            marginLeft: 'calc(-1 * (max((100vw - var(--learn-shell)) / 2, 0px) + 1.25rem))',
            width: 'calc(100% + max((100vw - var(--learn-shell)) / 2, 0px) + 1.25rem)',
          }}
        />
      </div>

      {/* The form — the only glass on the page. */}
      <div className="mx-auto w-full max-w-[460px]">
        <div className={cn(glassHero, 'px-6 py-8 sm:px-9 sm:py-9')}>
          {/* On phones the left column is gone, so the headline lives here. */}
          <h1 className="font-serif text-[34px] leading-[1.14] tracking-[-0.02em] lg:hidden">
            Lessons you <em className="italic text-[#5B2EE5] dark:text-[#B49BFF]">do</em>, not watch.
          </h1>
          <h2 className="font-serif text-[27px] leading-[1.28] tracking-tight max-lg:mt-4">{title}</h2>
          <p className={cn('mt-1.5 text-[14.5px] leading-relaxed', inkMuted)}>{sub}</p>

          {children}

          <p className={cn('mt-7 text-[12px] leading-relaxed', inkMuted)}>
            By continuing you agree to our{' '}
            <a href="/terms" className={cn('underline underline-offset-2 hover:text-[#5B2EE5] dark:hover:text-[#B49BFF]', FOCUS)}>terms</a>{' '}
            and{' '}
            <a href="/privacy" className={cn('underline underline-offset-2 hover:text-[#5B2EE5] dark:hover:text-[#B49BFF]', FOCUS)}>privacy policy</a>.
          </p>
        </div>
      </div>
    </div>
  );
}

